/**
 * Multi-Chat Page
 * Multi-Chat System: Main chat interface with dual sidebars
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Fab,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ListAlt as ListAltIcon,
  Add as AddIcon,
  DeleteSweep as DeleteSweepIcon,
  AutoFixHigh as AutoFixHighIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { useDialogState } from '@/hooks/ui/useDialogState';

// Chat components
import {
  ChatMessage,
  ChatInput,
  DeleteConfirmDialog,
} from '../components/chat';

// Conversation components
import {
  ConversationSidebar,
  DocumentReferencesSidebar,
} from '../components/conversation';

// API hooks
import {
  useGetConversation,
  useCreateConversation,
  useClearConversationMessages,
  useGenerateConversationTitle,
} from '../hooks/api/useConversations';
import { useSendMessage } from '../hooks/api/useConversationChat';

// State management
import { useConversationStore } from '../stores/conversationStore';
import { log } from '../utils/errors/logger';
import { useSystem } from '../hooks/api/config/useSystemOrganization';

/**
 * Helper to get appropriate error message for API errors
 */
const getErrorMessage = (error: unknown, t: any): string => {
  const isQuotaError =
    (error as any)?.status === 429 || (error as any)?.code === 'QUOTA_EXCEEDED';
  return isQuotaError ? t('common.errors.quotaExceeded') : t('chat.errorMessage');
};

const MultiChat: React.FC = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const dialogState = useDialogState();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { system } = useSystem();

  // Get conversation ID from URL params
  const { conversationId: currentConversationId } = useParams<{ conversationId?: string }>();

  // State management - UI preferences only
  const {
    conversationSidebarOpen,
    documentReferencesSidebarOpen,
    toggleConversationSidebar,
    toggleDocumentReferencesSidebar,
    setConversationSidebarOpen,
  } = useConversationStore();

  // Local state
  const [input, setInput] = useState('');
  const [streamingMessage, setStreamingMessage] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState(false);

  // API hooks
  const { data: currentConversation, isLoading: conversationLoading, error: conversationError } = useGetConversation(currentConversationId || null, {
    enabled: !!currentConversationId,
  });
  const createConversationMutation = useCreateConversation();
  const sendMessageMutation = useSendMessage();
  const clearMessagesMutation = useClearConversationMessages();
  const generateTitleMutation = useGenerateConversationTitle();

  // Scroll to bottom when messages change or during streaming
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [currentConversation?.messages, streamingMessage]);

  // Handle invalid conversation ID
  useEffect(() => {
    // Conversation ID exists but conversation not found (404 error)
    // This can happen if conversation was deleted on backend
    if (currentConversationId && conversationError && !conversationLoading && !createConversationMutation.isPending) {
      log.warn('Conversation not found, navigating to chat home', {
        invalidConversationId: currentConversationId,
        error: conversationError
      });

      // Navigate to chat home (no conversation ID)
      navigate('/chat', { replace: true });
      showError(t('conversation.notFound', { defaultValue: 'Conversation not found' }));
    }
  }, [currentConversationId, conversationLoading, conversationError, createConversationMutation.isPending, navigate, showError, t]);

  // Event handlers
  const handleCreateNewChat = async () => {
    try {
      log.user.action('create_new_chat', 'MultiChat');

      const newConversation = await createConversationMutation.mutateAsync();

      // Navigate to the new conversation
      navigate(`/chat/${newConversation.id}`);

      // Open conversation sidebar to show the new chat
      setConversationSidebarOpen(true);

      showSuccess(t('conversation.created', { title: newConversation.title }));
    } catch (error) {
      log.error('Failed to create conversation', error);
      showError(t('conversation.createFailed'));
    }
  };

  const handleSend = async () => {
    if (!input.trim()) {return;}

    const messageContent = input.trim();
    let conversationIdToUse = currentConversationId;
    
    // If no current conversation, create one first
    if (!conversationIdToUse) {
      try {
        log.info('Creating new conversation for first message');
        const newConversation = await createConversationMutation.mutateAsync();
        conversationIdToUse = newConversation.id;

        // Navigate to the new conversation
        navigate(`/chat/${newConversation.id}`, { replace: true });
        // Continue with sending the message after creating conversation
      } catch (error) {
        log.error('Failed to create conversation before sending message', error);
        showError(t('conversation.createFailed'));
        return;
      }
    }

    setInput('');
    setIsStreaming(true);
    setStreamingMessage('');

    // Scroll to bottom immediately when starting to send
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);

    try {
      log.user.action('send_message', 'MultiChat', { 
        conversationId: conversationIdToUse,
        contentLength: messageContent.length 
      });
      
      await sendMessageMutation.mutateAsync({
        conversationId: conversationIdToUse,
        content: messageContent,
        onChunk: (chunk: string, context?: any[]) => {
          if (chunk) {
            // Update streaming message with new chunk
            setStreamingMessage(prev => {
              const newContent = prev + chunk;
              // Auto-scroll during streaming for long responses
              setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
              }, 50);
              return newContent;
            });
          }
          // Context is handled by the mutation automatically
        }
      });

      // Auto-generate title if this is the first message
      if (!currentConversation || currentConversation?.message_count === 0) {
        setTimeout(() => {
          generateTitleMutation.mutate(conversationIdToUse);
        }, 1000); // Delay to ensure message is saved
      }
    } catch (error: any) {
      log.error('Failed to send message', error);
      
      // Check if it's a 404 error (conversation doesn't exist)
      if (error?.message?.includes('404') || error?.message?.includes('Not Found')) {
        log.warn('Conversation not found during send, creating new conversation', {
          invalidConversationId: currentConversationId
        });

        try {
          // Create new conversation and retry sending
          const newConversation = await createConversationMutation.mutateAsync();

          // Navigate to the new conversation
          navigate(`/chat/${newConversation.id}`, { replace: true });

          // Retry sending the message to the new conversation
          await sendMessageMutation.mutateAsync({
            conversationId: newConversation.id,
            content: messageContent,
            onChunk: (chunk: string, context?: any[]) => {
              if (chunk) {
                setStreamingMessage(prev => {
                  const newContent = prev + chunk;
                  setTimeout(() => {
                    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                  }, 50);
                  return newContent;
                });
              }
            }
          });

          showSuccess(t('conversation.created', { title: newConversation.title }));
        } catch (retryError) {
          log.error('Failed to create new conversation and retry message', retryError);
          showError(getErrorMessage(retryError, t));
          setInput(messageContent); // Restore input on error
        }
      } else {
        showError(getErrorMessage(error, t));
        setInput(messageContent); // Restore input on error
      }
    } finally {
      setIsStreaming(false);
      setStreamingMessage('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClearConversation = async () => {
    if (!currentConversationId) {return;}

    try {
      await clearMessagesMutation.mutateAsync(currentConversationId);
      showSuccess(t('conversation.messagesCleared'));
      dialogState.closeDeleteConfirm();
      
      log.user.action('clear_conversation', 'MultiChat', { conversationId: currentConversationId });
    } catch (error) {
      log.error('Failed to clear conversation', error);
      showError(t('conversation.clearFailed'));
    }
  };

  // Component logging
  useEffect(() => {
    log.component.mount('MultiChat', { currentConversationId });
  }, []);

  const messages = currentConversation?.messages || [];
  const isLoading = conversationLoading || sendMessageMutation.isPending;

  return (
    <Box
      id="multi-chat-container"
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Left: Conversation Sidebar */}
      <Box
        id="conversation-sidebar-container"
        sx={{
          width: conversationSidebarOpen ? 300 : 0,
          flexShrink: 0,
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          borderRight: conversationSidebarOpen ? 1 : 0,
          borderColor: 'divider',
        }}
      >
        <ConversationSidebar
          open={conversationSidebarOpen}
          onClose={() => setConversationSidebarOpen(false)}
        />
      </Box>
      
      {/* Center: Main Chat Area */}
      <Box 
        id="main-chat-area"
        component="main" 
        sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          overflow: 'hidden',
          minWidth: 0, // Prevents flex item from overflowing
        }}
      >
        {/* Messages Area */}
        <Box 
          id="chat-messages-area"
          sx={{ 
            flexGrow: 1, 
            overflow: 'auto', 
            p: 2,
            bgcolor: '#f8f8f8',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Empty State */}
          {messages.length === 0 && !conversationLoading && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100%',
              p: 3 
            }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#003064',
                  mb: 2,
                  fontWeight: 500
                }} 
                align="center"
              >
                {t('chat.welcome', { systemName: system?.name || 'smartDoc' })}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ color: '#666666' }} 
                align="center"
              >
                {t('chat.askAnything')}
              </Typography>
            </Box>
          )}

          {/* Messages */}
          {messages.map((message, index) => (
            <ChatMessage
              key={message.id || index}
              message={message}
              isUser={message.role === 'user'}
            />
          ))}

          {/* Streaming Message */}
          {isStreaming && streamingMessage && (
            <ChatMessage
              key="streaming-message"
              message={{
                role: 'assistant',
                content: streamingMessage,
                sources: []
              }}
              isUser={false}
            />
          )}

          {/* Loading Indicator */}
          {(isLoading || (isStreaming && !streamingMessage)) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress size={24} sx={{ color: '#003064' }} />
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {/* Fixed Bottom Input Area */}
        <Box 
          id="chat-input-area"
          sx={{ 
            borderTop: 1, 
            borderColor: 'divider',
            bgcolor: 'background.paper',
            p: 2,
          }}
        >
          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'space-between',
              mb: 2,
              pb: 1.5,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            {/* Left side buttons */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <Tooltip title={t('conversation.conversations')} arrow>
                <IconButton
                  onClick={toggleConversationSidebar}
                  color={conversationSidebarOpen ? 'primary' : 'default'}
                  sx={{
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      bgcolor: conversationSidebarOpen ? 'primary.lighter' : 'action.hover',
                    },
                  }}
                >
                  <MenuIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title={t('conversation.new')} arrow>
                <span>
                  <Fab
                    size="small"
                    color="primary"
                    onClick={handleCreateNewChat}
                    disabled={createConversationMutation.isPending}
                    sx={{
                      boxShadow: 2,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 4,
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                      },
                    }}
                  >
                    <AddIcon />
                  </Fab>
                </span>
              </Tooltip>

              {/* TODO: Commented out manual title generation button - auto-generation still works on first message */}
              {/* {currentConversationId && messages.length > 0 && (
                <Tooltip title={t('conversation.generateTitle')} arrow>
                  <span>
                    <Fab
                      size="small"
                      color="secondary"
                      onClick={() => generateTitleMutation.mutate(currentConversationId)}
                      disabled={generateTitleMutation.isPending}
                      sx={{
                        boxShadow: 2,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 4,
                        },
                        '&:active': {
                          transform: 'translateY(0)',
                        },
                      }}
                    >
                      <AutoFixHighIcon />
                    </Fab>
                  </span>
                </Tooltip>
              )} */}
            </Box>

            {/* Right side buttons */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <Tooltip title={t('conversation.documentReferences')} arrow>
                <IconButton
                  onClick={toggleDocumentReferencesSidebar}
                  color={documentReferencesSidebarOpen ? 'primary' : 'default'}
                  sx={{
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      bgcolor: documentReferencesSidebarOpen ? 'primary.lighter' : 'action.hover',
                    },
                  }}
                >
                  <ListAltIcon />
                </IconButton>
              </Tooltip>

              {messages.length > 0 && (
                <Tooltip title={t('chat.clearHistory')} arrow>
                  <IconButton
                    onClick={() => dialogState.openDeleteConfirm({ id: currentConversationId } as any)}
                    color="error"
                    disabled={clearMessagesMutation.isPending}
                    sx={{
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        bgcolor: 'error.lighter',
                      },
                    }}
                  >
                    <DeleteSweepIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Chat Input */}
          <ChatInput
            input={input}
            loading={isLoading}
            onInputChange={setInput}
            onSend={handleSend}
            onKeyPress={handleKeyPress}
            disabled={false}
          />
        </Box>
      </Box>

      {/* Right: Document References Sidebar */}
      <Box
        id="document-references-sidebar-container"
        sx={{
          width: documentReferencesSidebarOpen ? 320 : 0,
          flexShrink: 0,
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          borderLeft: documentReferencesSidebarOpen ? 1 : 0,
          borderColor: 'divider',
        }}
      >
        <DocumentReferencesSidebar
          open={documentReferencesSidebarOpen}
          onClose={() => useConversationStore.setState({ documentReferencesSidebarOpen: false })}
        />
      </Box>
      
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={dialogState.deleteConfirmOpen}
        deleting={clearMessagesMutation.isPending}
        onClose={dialogState.closeDeleteConfirm}
        onConfirm={handleClearConversation}
      />
    </Box>
  );
};

export default MultiChat;